/**
 * Netflix-style carousel with autoplay, touch/swipe, and arrow navigation.
 */
(function () {
  'use strict';

  function initCarousel(el) {
    const track = el.querySelector('.netflix-carousel__track');
    const viewport = el.querySelector('.netflix-carousel__viewport');
    const prevBtn = el.querySelector('.netflix-carousel__arrow--prev');
    const nextBtn = el.querySelector('.netflix-carousel__arrow--next');
    const dots = el.querySelectorAll('.netflix-carousel__dot');

    if (!track || !viewport) return;

    const slides = Array.from(track.querySelectorAll('.netflix-carousel__slide:not(.netflix-carousel__slide--clone)'));
    const total = slides.length;
    if (total === 0) return;

    const autoplay = el.dataset.autoplay === 'true';
    const speed = Math.max(1, parseInt(el.dataset.speed, 10) || 4) * 1000;
    const direction = el.dataset.direction === 'right' ? -1 : 1;
    const pauseOnHover = el.dataset.pauseOnHover === 'true';

    let currentIndex = 0;
    let autoplayTimer = null;
    let isPaused = false;

    // ── Responsive cards count ───────────────────────────────────────────

    function getCardsVisible() {
      const w = window.innerWidth;
      const style = getComputedStyle(el);
      if (w <= 767) return parseInt(style.getPropertyValue('--cards-mobile'), 10) || 2;
      if (w <= 1024) return parseInt(style.getPropertyValue('--cards-tablet'), 10) || 3;
      return parseInt(style.getPropertyValue('--cards-desktop'), 10) || 4;
    }

    // ── Slide width calculation ──────────────────────────────────────────

    function getSlideWidth() {
      const firstSlide = track.querySelector('.netflix-carousel__slide');
      if (firstSlide) return firstSlide.offsetWidth;
      return 0;
    }

    function getGap() {
      return parseFloat(getComputedStyle(track).gap) || 0;
    }

    // ── Go to index (clamped, or wraps for infinite) ─────────────────────

    function goTo(index, animate = true) {
      const maxIndex = total - getCardsVisible();
      if (index < 0) index = Math.max(0, maxIndex);
      else if (index > maxIndex) index = 0;

      currentIndex = index;

      if (!animate) track.classList.add('is-dragging');

      const slideWidth = getSlideWidth();
      const gap = getGap();
      const offset = -(currentIndex * (slideWidth + gap));
      track.style.transform = `translateX(${offset}px)`;

      if (!animate) {
        // Force reflow then restore transition
        track.offsetHeight; // eslint-disable-line no-unused-expressions
        track.classList.remove('is-dragging');
      }

      updateDots();
      updateArrows();
    }

    function updateDots() {
      dots.forEach((dot, i) => {
        const active = i === currentIndex;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', String(active));
      });
    }

    function updateArrows() {
      if (!prevBtn || !nextBtn) return;
      // Always enabled for looping carousels
    }

    // ── Autoplay ─────────────────────────────────────────────────────────

    function startAutoplay() {
      if (!autoplay) return;
      stopAutoplay();
      autoplayTimer = setInterval(() => {
        if (!isPaused) goTo(currentIndex + direction);
      }, speed);
    }

    function stopAutoplay() {
      if (autoplayTimer) {
        clearInterval(autoplayTimer);
        autoplayTimer = null;
      }
    }

    // ── Arrow navigation ─────────────────────────────────────────────────

    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        goTo(currentIndex - 1);
        stopAutoplay();
        startAutoplay();
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        goTo(currentIndex + 1);
        stopAutoplay();
        startAutoplay();
      });
    }

    // ── Dot navigation ───────────────────────────────────────────────────

    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const index = parseInt(dot.dataset.index, 10);
        if (!isNaN(index)) {
          goTo(index);
          stopAutoplay();
          startAutoplay();
        }
      });
    });

    // ── Touch / swipe ────────────────────────────────────────────────────

    let touchStartX = 0;
    let touchStartY = 0;
    let touchDeltaX = 0;
    let isDragging = false;
    let isScrolling = false;

    viewport.addEventListener('touchstart', (e) => {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
      touchDeltaX = 0;
      isDragging = false;
      isScrolling = false;
      stopAutoplay();
    }, { passive: true });

    viewport.addEventListener('touchmove', (e) => {
      if (isScrolling) return;

      const dx = e.touches[0].clientX - touchStartX;
      const dy = e.touches[0].clientY - touchStartY;

      if (!isDragging && Math.abs(dy) > Math.abs(dx)) {
        isScrolling = true;
        return;
      }

      isDragging = true;
      touchDeltaX = dx;

      const slideWidth = getSlideWidth();
      const gap = getGap();
      const baseOffset = -(currentIndex * (slideWidth + gap));
      track.classList.add('is-dragging');
      track.style.transform = `translateX(${baseOffset + touchDeltaX}px)`;
    }, { passive: true });

    viewport.addEventListener('touchend', () => {
      track.classList.remove('is-dragging');

      if (isDragging) {
        const threshold = getSlideWidth() * 0.2;
        if (touchDeltaX < -threshold) {
          goTo(currentIndex + 1);
        } else if (touchDeltaX > threshold) {
          goTo(currentIndex - 1);
        } else {
          goTo(currentIndex); // snap back
        }
      }

      isDragging = false;
      startAutoplay();
    }, { passive: true });

    // ── Mouse drag (desktop) ─────────────────────────────────────────────

    let mouseStartX = 0;
    let mouseDeltaX = 0;
    let isMouseDragging = false;

    viewport.addEventListener('mousedown', (e) => {
      if (e.button !== 0) return;
      mouseStartX = e.clientX;
      mouseDeltaX = 0;
      isMouseDragging = true;
      track.classList.add('is-dragging');
      stopAutoplay();
      e.preventDefault();
    });

    window.addEventListener('mousemove', (e) => {
      if (!isMouseDragging) return;
      mouseDeltaX = e.clientX - mouseStartX;
      const slideWidth = getSlideWidth();
      const gap = getGap();
      const baseOffset = -(currentIndex * (slideWidth + gap));
      track.style.transform = `translateX(${baseOffset + mouseDeltaX}px)`;
    });

    window.addEventListener('mouseup', () => {
      if (!isMouseDragging) return;
      isMouseDragging = false;
      track.classList.remove('is-dragging');

      const threshold = getSlideWidth() * 0.15;
      if (mouseDeltaX < -threshold) {
        goTo(currentIndex + 1);
      } else if (mouseDeltaX > threshold) {
        goTo(currentIndex - 1);
      } else {
        goTo(currentIndex);
      }

      startAutoplay();
    });

    // Prevent click-through on drag
    viewport.addEventListener('click', (e) => {
      if (Math.abs(mouseDeltaX) > 5) {
        e.preventDefault();
        e.stopPropagation();
        mouseDeltaX = 0;
      }
    }, true);

    // ── Pause on hover ───────────────────────────────────────────────────

    if (pauseOnHover) {
      el.addEventListener('mouseenter', () => { isPaused = true; });
      el.addEventListener('mouseleave', () => { isPaused = false; });
    }

    // ── Keyboard support ─────────────────────────────────────────────────

    el.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        goTo(currentIndex - 1);
        stopAutoplay();
        startAutoplay();
      } else if (e.key === 'ArrowRight') {
        goTo(currentIndex + 1);
        stopAutoplay();
        startAutoplay();
      }
    });

    // ── Resize handler ───────────────────────────────────────────────────

    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => goTo(currentIndex, false), 100);
    });

    // ── Intersection Observer — pause when off-screen ────────────────────

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              startAutoplay();
            } else {
              stopAutoplay();
            }
          });
        },
        { threshold: 0.1 }
      );
      observer.observe(el);
    } else {
      startAutoplay();
    }

    // ── Init ─────────────────────────────────────────────────────────────

    goTo(0, false);
  }

  // ── Boot ─────────────────────────────────────────────────────────────────

  function boot() {
    document.querySelectorAll('.netflix-carousel').forEach(initCarousel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-init on Shopify section reload in the theme editor
  document.addEventListener('shopify:section:load', (e) => {
    const section = e.target.querySelector('.netflix-carousel');
    if (section) initCarousel(section);
  });
})();
